import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, MailWarning, Paperclip, Trash2, UserCheck } from 'lucide-react';
import { useHeldEmails, useReleaseHeldEmail, useDiscardHeldEmail } from '../hooks/useHeldEmails';
import { useHelpdeskNavigation } from '../hooks/useHelpdeskNavigation';
import { useDateFormat } from '../hooks/useDateFormat';
import { showToast } from '../lib/toast';
import { getErrorMessage } from '../lib/apiErrors';
import { InboundEmailStatus, type HeldEmailResponse } from '../types/heldEmails';

interface HeldEmailsSettingsProps {
  organizationId?: string;
}

/**
 * Email from archived customers (Org Settings → Held Emails, admins). Writing in doesn't give an archived customer
 * access back: their email waits here until an admin restores them and creates the ticket, or discards it.
 */
export const HeldEmailsSettings: React.FC<HeldEmailsSettingsProps> = ({ organizationId }) => {
  const { data: emails = [], isLoading, error } = useHeldEmails(organizationId);
  const releaseMutation = useReleaseHeldEmail();
  const discardMutation = useDiscardHeldEmail();
  const { getPath, navigateTo } = useHelpdeskNavigation();
  const { formatDateTime } = useDateFormat();

  const senderName = (email: HeldEmailResponse) => email.customerName || email.fromName || email.fromEmail;

  const handleRelease = async (email: HeldEmailResponse) => {
    try {
      const result = await releaseMutation.mutateAsync(email.id);
      if (result.status === InboundEmailStatus.Processed && result.ticketId) {
        showToast.success(
          'Customer Restored',
          `${senderName(email)} is active again and the email is on ticket #${result.ticketNumber}.`
        );
        navigateTo.ticket(result.ticketId);
      } else if (result.status === InboundEmailStatus.Failed) {
        showToast.error(
          'Customer Restored',
          `${senderName(email)} is active again, but the email couldn't be processed yet. It will be retried automatically.`
        );
      } else {
        showToast.success('Customer Restored', `${senderName(email)} is active again.`);
      }
    } catch (err) {
      showToast.error('Restore Failed', getErrorMessage(err, 'Failed to restore the customer.'));
    }
  };

  const handleDiscard = async (email: HeldEmailResponse) => {
    try {
      await discardMutation.mutateAsync(email.id);
      showToast.success('Email Discarded', `The email from ${senderName(email)} was deleted.`);
    } catch (err) {
      showToast.error('Discard Failed', getErrorMessage(err, 'Failed to discard the email.'));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-semibold">Held Emails</h3>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Email from archived customers. Writing in doesn't give an archived customer access again: restore them to
          create the ticket, or discard the email. Held emails are deleted after 30 days.
        </p>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <MailWarning className="h-4 w-4 sm:h-5 sm:w-5" />
            Held ({emails.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading held emails...
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{getErrorMessage(error, 'Failed to load held emails.')}</p>
          ) : emails.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No held emails.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden sm:table-cell">Received</TableHead>
                    <TableHead className="hidden md:table-cell">Deleted on</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id}>
                      <TableCell>
                        <div className="min-w-0">
                          {email.customerId ? (
                            <Link
                              to={getPath.customer(email.customerId)}
                              className="font-medium text-sm hover:underline"
                            >
                              {senderName(email)}
                            </Link>
                          ) : (
                            <p className="font-medium text-sm">{senderName(email)}</p>
                          )}
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={email.fromEmail}>
                            {email.fromEmail}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate max-w-[260px]" title={email.subject}>
                            {email.subject || '(no subject)'}
                            {email.attachmentCount > 0 && (
                              <span className="inline-flex items-center gap-0.5 ml-1 text-xs text-muted-foreground align-middle">
                                <Paperclip className="h-3 w-3" />
                                {email.attachmentCount}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2 max-w-[360px]">{email.preview}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">
                        {formatDateTime(email.receivedAt)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                        {formatDateTime(email.deleteAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                disabled={releaseMutation.isPending}
                                title="Restore customer and create ticket"
                                aria-label={`Restore ${senderName(email)} and create the ticket`}
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Restore {senderName(email)}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  They become an active customer again, able to sign in to the customer portal and
                                  email in, and this email is turned into a ticket or added to the ticket it replies
                                  to.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRelease(email)}>
                                  Restore and Create Ticket
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                disabled={discardMutation.isPending}
                                title="Discard"
                                aria-label={`Discard the email from ${senderName(email)}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Discard this email?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  The email from {senderName(email)} is deleted now instead of after 30 days, and the
                                  customer stays archived. This can't be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDiscard(email)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Discard
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
